/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect } from 'react'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import SessionAction from 'store/session/SessionAction'
import { STORAGE_KEYS } from 'constants/StorageKeys'
import { LocalStorage } from 'utils/LocalStorage'

import { UiThemeContext, UiThemeMode } from './UiThemeContext'

interface UiThemeProviderProps {
    children: React.ReactNode
}

export const resolveInitialThemeMode = (): UiThemeMode => {
    const stored = LocalStorage.get<UiThemeMode>(STORAGE_KEYS.UI_THEME_MODE)
    if (stored === 'light' || stored === 'dark') {
        return stored
    }

    return 'light'
}

const UiThemeProvider: React.FC<UiThemeProviderProps> = ({ children }) => {
    const dispatch = useAppDispatch()
    const modeFromStore = useTypedSelector((state) => state.sessionState.themeMode)

    const toggleMode = () => {
        const current = modeFromStore || resolveInitialThemeMode()
        const nextMode: UiThemeMode = current === 'light' ? 'dark' : 'light'
        dispatch(SessionAction.setThemeMode(nextMode))
    }

    useEffect(() => {
        if (!modeFromStore) {
            dispatch(SessionAction.setThemeMode(resolveInitialThemeMode()))
        }
    }, [modeFromStore, dispatch])

    useEffect(() => {
        const rootElement = document.documentElement
        const activeMode = modeFromStore || resolveInitialThemeMode()
        if (activeMode === 'dark') {
            rootElement.classList.add('dark')
        } else {
            rootElement.classList.remove('dark')
        }
        LocalStorage.set(STORAGE_KEYS.UI_THEME_MODE, activeMode)
    }, [modeFromStore])

    return (
        <UiThemeContext.Provider value={{ mode: modeFromStore || resolveInitialThemeMode(), toggleMode }}>
            {children}
        </UiThemeContext.Provider>
    )
}

export {useUiTheme} from './useUiTheme'

export default UiThemeProvider
